import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import classnames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { TimerIcon } from 'assets';
import { selectCall } from 'store/call';
import { useAppSelector } from 'store';

import styles from './SessionTimerButton.module.scss';

type SessionTimerButtonProps = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  iconColor?: string;
  withTooltip?: boolean;
  withLabel?: boolean;
};

export const SessionTimerButton = forwardRef<HTMLButtonElement, SessionTimerButtonProps>(
  ({ disabled, className, iconClassName, iconColor, withLabel }: SessionTimerButtonProps, ref) => {
    const { t } = useTranslation(['providerRoom', 'settings']);
    const [open, setOpen] = useState<boolean>(true);
    const [seconds, setSeconds] = useState<number>(0);
    const call = useAppSelector(selectCall);

    const sx = useMemo(
      () => ({
        backgroundColor: withLabel ? 'transparent !important' : 'rgb(0 0 0 / 50%) !important',
      }),
      [withLabel],
    );

    const timeString: string = useMemo(() => {
      const hours: number = Math.floor(seconds / 3600);
      const minutes: number = Math.floor((seconds % 3600) / 60);
      const secs: number = Math.floor(seconds % 60);

      if (hours > 0) {
        return `${hours}${t('settings:callHistory.hoursShort')} ${minutes}${t(
          'settings:callHistory.minutesShort',
        )} ${secs}${t('settings:callHistory.secondsShort')}`;
      }

      return `${minutes}${t('settings:callHistory.minutesShort')} ${secs}${t('settings:callHistory.secondsShort')}`;
    }, [seconds, t]);

    useEffect(() => {
      if (!call?.startDate) {
        setSeconds(0);
        return;
      }

      const startDateUtc = moment.utc(call.startDate, moment.ISO_8601, true);
      if (!startDateUtc.isValid()) {
        setSeconds(0);
        return;
      }

      const effectiveStartUtc = startDateUtc.isAfter(moment.utc()) ? moment.utc() : startDateUtc;

      const updateTimer = () => {
        const elapsedSeconds = Math.max(0, moment.utc().diff(effectiveStartUtc, 'seconds'));
        setSeconds(elapsedSeconds);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }, [call?.startDate]);

    const handleClick = (): void => {
      if (!withLabel) {
        setOpen(!open);
      }
    };

    return (
      <Box component='span' display='block' className={classnames(styles.deviceButton, className)}>
        <IconButton
          disabled={disabled}
          className={classnames(
            {
              [styles.deviceButton]: !withLabel,
              [styles.deviceButtonOpen]: open && !withLabel,
              [styles.deviceButtonWithLabel]: withLabel,
            },
            className,
          )}
          sx={sx}
          onClick={handleClick}
          edge='end'
          aria-label={t('aria.sessionTimer')}
          ref={ref}
        >
          {open && !withLabel && (
            <>
              <TimerIcon className={styles.icon} sx={{ color: iconColor || '#fff' }} />
              <Typography component='p' variant='body7' color='white'>
                {timeString}
              </Typography>
            </>
          )}
          {(!open || withLabel) && (
            <TimerIcon className={classnames(styles.icon, iconClassName)} sx={{ color: iconColor || '#fff' }} />
          )}
          {withLabel && (
            <Typography component='p' variant='body2' sx={{ color: 'var(--app-text-default)' }} ml='10px'>
              {timeString}
            </Typography>
          )}
        </IconButton>
      </Box>
    );
  },
);
